import React, {useState} from 'react'
import Text from '../../components/text/Text'
import UnderLineInput from '../../components/input/UnderLineInput'
import WizSelect from '../../components/select/WizSelect'
import WizButton from '../../components/button/WizButton'
import WizImage from '../../components/image/WizImage'
import ic_btn_next from '../../assets/images/ic_btn_next.svg'
import RichParagraphRun from './RichParagraphRun'
import ImageBlockField from './ImageBlockField'
import VariableChipList from './VariableChipList'
import {BLOCK_TYPE_LABEL, BLOCK_TYPE_OPTIONS, STYLE_TOKEN_OPTIONS} from './constants'
import {ContentBlockType, ICaseVariable, IContentBlock} from './types'
import {newBlockByType, newSegment} from './utils'

interface IProps {
    className?: string
    blocks: IContentBlock[]
    onChange: (blocks: IContentBlock[]) => void
    depth?: number // 0 = 최상위, 1+ = container 중첩(표시용)
    variables?: ICaseVariable[] // 현재 카테고리의 기본 변수 셋
}

type Segment = {kind: 'run'; start: number; end: number} | {kind: 'single'; start: number; end: number}

const NONE_STYLE_OPTION = {id: '', value: '없음'}

// 연속된 paragraph는 하나의 런(RichParagraphRun)으로 묶고, 그 외는 독립된 단일 세그먼트로 다룬다.
const buildSegments = (blocks: IContentBlock[]): Segment[] => {
    const segments: Segment[] = []
    let i = 0
    while (i < blocks.length) {
        if (blocks[i].type === 'paragraph') {
            let j = i
            while (j < blocks.length && blocks[j].type === 'paragraph') j++
            segments.push({kind: 'run', start: i, end: j})
            i = j
        } else {
            segments.push({kind: 'single', start: i, end: i + 1})
            i++
        }
    }
    return segments
}

// ── container/chart/tag/icon 속성 select 옵션 (id '' = 미지정/undefined) ──────────
const VARIANT_OPTIONS = [
    {id: '', value: '없음(박스 없음)'},
    {id: 'card', value: 'card(카드)'},
    {id: 'tile', value: 'tile(타일)'},
]
const BACKGROUND_OPTIONS = [
    {id: '', value: '없음'},
    {id: 'neutral', value: 'neutral(기본)'},
    {id: 'skyblue', value: 'skyblue(차가움)'},
    {id: 'positive', value: 'positive(강조)'},
    {id: 'orange', value: 'orange(따뜻함)'},
    {id: 'negative', value: 'negative'},
]
const DIRECTION_OPTIONS = [
    {id: '', value: '기본(column)'},
    {id: 'column', value: 'column(세로)'},
    {id: 'row', value: 'row(가로)'},
]
const ALIGN_OPTIONS = [
    {id: '', value: 'start(기본)'},
    {id: 'center', value: 'center'},
    {id: 'end', value: 'end'},
]
const JUSTIFY_OPTIONS = [
    {id: '', value: 'start(기본)'},
    {id: 'center', value: 'center'},
    {id: 'end', value: 'end'},
    {id: 'between', value: 'between'},
    {id: 'around', value: 'around'},
    {id: 'evenly', value: 'evenly'},
]
const GAP_OPTIONS = [
    {id: '', value: '기본'},
    {id: 'none', value: 'none'},
    {id: 'xs', value: 'xs(4px)'},
    {id: 'sm', value: 'sm(8px)'},
    {id: 'md', value: 'md(12px)'},
    {id: 'lg', value: 'lg(16px)'},
]
const CHART_VARIANT_OPTIONS = [
    {id: 'bar', value: 'bar(막대)'},
    {id: 'donut', value: 'donut(도넛)'},
]
const TAG_VARIANT_OPTIONS = [
    {id: 'primary', value: 'primary'},
    {id: 'secondary', value: 'secondary'},
    {id: 'tertiary', value: 'tertiary(기본)'},
    {id: 'negative', value: 'negative'},
]
const ICON_SIZE_OPTIONS = [
    {id: 'sm', value: 'sm(20px)'},
    {id: 'md', value: 'md(32px)'},
    {id: 'lg', value: 'lg(56px)'},
]

const ContentBlockBuilder: React.FC<IProps> = ({className, blocks, onChange, depth = 0, variables}) => {
    const styleOptions = [NONE_STYLE_OPTION, ...STYLE_TOKEN_OPTIONS]
    const segments = buildSegments(blocks)
    const [typeToAdd, setTypeToAdd] = useState<ContentBlockType>(BLOCK_TYPE_OPTIONS[0]?.id)
    // 버튼에서 마지막으로 focus된 입력(텍스트/URL) — 변수 칩을 눌렀을 때 이 필드에 삽입한다.
    const [buttonFocusField, setButtonFocusField] = useState<'text' | 'url'>('text')

    const updateSingleBlock = (index: number, patch: Partial<IContentBlock>) => {
        onChange(blocks.map((block, i) => (i === index ? {...block, ...patch} : block)))
    }

    const removeSegment = (segment: Segment) => {
        onChange([...blocks.slice(0, segment.start), ...blocks.slice(segment.end)])
    }

    const moveSegment = (segIndex: number, dir: -1 | 1) => {
        const targetIndex = segIndex + dir
        if (targetIndex < 0 || targetIndex >= segments.length) return
        const a = segments[segIndex]
        const b = segments[targetIndex]
        const first = dir === 1 ? a : b
        const second = dir === 1 ? b : a
        const before = blocks.slice(0, first.start)
        const after = blocks.slice(second.end)
        const firstBlocks = blocks.slice(first.start, first.end)
        const secondBlocks = blocks.slice(second.start, second.end)
        onChange([...before, ...secondBlocks, ...firstBlocks, ...after])
    }

    const addBlock = (type: ContentBlockType) => {
        onChange([...blocks, newBlockByType(type)])
    }

    const segmentKeyOf = (segment: Segment) => blocks[segment.start]?.id

    // 라벨 + WizSelect 한 줄. id '' 선택 시 undefined로 저장한다.
    const renderSelect = (
        label: string,
        options: {id: string; value: string}[],
        current: string | undefined,
        onPick: (id: string | undefined) => void,
        buttonClassName = 'min-w-[150px]',
    ) => (
        <div className={'flex items-center gap-x-[8px]'}>
            <Text className={'text-ti3 text-font03 w-[80px] shrink-0'}>{label}</Text>
            <WizSelect
                customOption={options}
                buttonClassName={buttonClassName}
                value={options.find(o => o.id === (current ?? ''))?.value}
                onChange={value => {
                    const id = options.find(o => o.value === value)?.id
                    onPick(id ? id : undefined)
                }}
            />
        </div>
    )

    const renderContainer = (block: IContentBlock, index: number) => (
        <div className={'space-y-[10px]'}>
            <div className={'grid grid-cols-2 gap-[8px]'}>
                {renderSelect('variant', VARIANT_OPTIONS, block.variant, variant =>
                    updateSingleBlock(index, {variant}),
                )}
                {renderSelect('background', BACKGROUND_OPTIONS, block.background, background =>
                    updateSingleBlock(index, {background: background as IContentBlock['background']}),
                )}
                {renderSelect('direction', DIRECTION_OPTIONS, block.direction, direction =>
                    updateSingleBlock(index, {direction: direction as IContentBlock['direction']}),
                )}
                {renderSelect('gap', GAP_OPTIONS, block.gap, gap =>
                    updateSingleBlock(index, {gap: gap as IContentBlock['gap']}),
                )}
                {renderSelect('align', ALIGN_OPTIONS, block.align, align =>
                    updateSingleBlock(index, {align: align as IContentBlock['align']}),
                )}
                {renderSelect('justify', JUSTIFY_OPTIONS, block.justify, justify =>
                    updateSingleBlock(index, {justify: justify as IContentBlock['justify']}),
                )}
            </div>
            <div className={'flex items-center gap-x-[16px]'}>
                <label className={'flex items-center gap-x-[6px] text-ti3 text-font03 cursor-pointer select-none'}>
                    <input
                        type={'checkbox'}
                        checked={block.flex === 'grow'}
                        onChange={e =>
                            onChange(
                                blocks.map((b, i) => {
                                    if (i !== index) return b
                                    // 체크 시 flex:"grow" 부여, 해제 시 필드 자체를 제거(방출 안 함).
                                    if (e.target.checked) return {...b, flex: 'grow' as const}
                                    const next = {...b}
                                    delete next.flex
                                    return next
                                }),
                            )
                        }
                    />
                    flex grow 적용
                </label>
            </div>
            <UnderLineInput
                placeholder={'url — 있으면 컨테이너 전체가 탭 가능 (optional)'}
                inputClassName={'w-full'}
                value={block.url || ''}
                onChanged={url => updateSingleBlock(index, {url})}
            />
            <Text className={'text-body3 text-font02'}>컨테이너 내부 블록 (blocks)</Text>
            <ContentBlockBuilder
                blocks={block.blocks || []}
                onChange={childBlocks => updateSingleBlock(index, {blocks: childBlocks})}
                depth={depth + 1}
                variables={variables}
            />
        </div>
    )

    const renderChart = (block: IContentBlock, index: number) => {
        const total = (block.segments || []).reduce((sum, s) => sum + (s.percent || 0), 0)
        return (
            <div className={'space-y-[10px]'}>
                {renderSelect('variant', CHART_VARIANT_OPTIONS, block.variant ?? 'bar', variant =>
                    updateSingleBlock(index, {variant}),
                )}
                <UnderLineInput
                    placeholder={'text — 도넛 범례 위 제목 (막대에는 미사용, optional)'}
                    inputClassName={'w-full'}
                    value={block.text || ''}
                    onChanged={text => updateSingleBlock(index, {text})}
                />
                {(block.segments || []).map((segment, segIndex) => (
                    <div key={segment.id} className={'flex items-center gap-x-[10px]'}>
                        <UnderLineInput
                            placeholder={'label'}
                            inputClassName={'w-full'}
                            value={segment.label}
                            onChanged={label =>
                                updateSingleBlock(index, {
                                    segments: (block.segments || []).map((s, i) =>
                                        i === segIndex ? {...s, label} : s,
                                    ),
                                })
                            }
                        />
                        <UnderLineInput
                            type={'number'}
                            placeholder={'percent'}
                            inputClassName={'w-[100px]'}
                            value={segment.percent}
                            onChanged={percent =>
                                updateSingleBlock(index, {
                                    segments: (block.segments || []).map((s, i) =>
                                        i === segIndex ? {...s, percent: Number(percent) || 0} : s,
                                    ),
                                })
                            }
                        />
                        <WizSelect
                            customOption={styleOptions}
                            value={styleOptions.find(o => o.id === (segment.style || ''))?.value}
                            onChange={value => {
                                const id = styleOptions.find(o => o.value === value)?.id
                                updateSingleBlock(index, {
                                    segments: (block.segments || []).map((s, i) =>
                                        i === segIndex ? {...s, style: (id || undefined) as IContentBlock['style']} : s,
                                    ),
                                })
                            }}
                        />
                        <WizButton
                            size={'small'}
                            type={'red'}
                            text={'삭제'}
                            onClick={() =>
                                updateSingleBlock(index, {
                                    segments: (block.segments || []).filter((_, i) => i !== segIndex),
                                })
                            }
                        />
                    </div>
                ))}
                <div className={'flex items-center justify-between'}>
                    <Text className={'text-ti3 text-font03'}>
                        percent 합계: {total}
                        {total !== 100 && ' (100이 아님)'}
                    </Text>
                    <WizButton
                        size={'small'}
                        type={'border'}
                        text={'구간 추가'}
                        onClick={() => updateSingleBlock(index, {segments: [...(block.segments || []), newSegment()]})}
                    />
                </div>
            </div>
        )
    }

    const renderTag = (block: IContentBlock, index: number) => (
        <div className={'space-y-[10px]'}>
            <UnderLineInput
                placeholder={'라벨 텍스트 (없으면 스킵됨)'}
                inputClassName={'w-full'}
                value={block.text || ''}
                onChanged={text => updateSingleBlock(index, {text})}
            />
            {renderSelect('variant', TAG_VARIANT_OPTIONS, block.variant ?? 'tertiary', variant =>
                updateSingleBlock(index, {variant}),
            )}
        </div>
    )

    const renderIcon = (block: IContentBlock, index: number) => (
        <div className={'space-y-[10px]'}>
            <UnderLineInput
                placeholder={'icon_url (이미지 URL)'}
                inputClassName={'w-full'}
                value={block.icon_url || ''}
                onChanged={icon_url => updateSingleBlock(index, {icon_url})}
            />
            {renderSelect('size', ICON_SIZE_OPTIONS, block.size ?? 'md', size =>
                updateSingleBlock(index, {size: size as IContentBlock['size']}),
            )}
        </div>
    )

    const renderButton = (block: IContentBlock, index: number) => {
        const insertVariable = (name: string) => {
            if (buttonFocusField === 'url') updateSingleBlock(index, {url: `${block.url || ''}{${name}}`})
            else updateSingleBlock(index, {text: `${block.text || ''}{${name}}`})
        }
        return (
            <div className={'space-y-[8px]'}>
                <UnderLineInput
                    placeholder={'버튼 텍스트 (예: 포지션 보기, {TRADER_NAME})'}
                    inputClassName={'w-full'}
                    value={block.text || ''}
                    onFocus={() => setButtonFocusField('text')}
                    onChanged={text => updateSingleBlock(index, {text})}
                />
                <UnderLineInput
                    placeholder={'이동 URL (예: https://.../position/{TRADER_ID})'}
                    inputClassName={'w-full'}
                    value={block.url || ''}
                    onFocus={() => setButtonFocusField('url')}
                    onChanged={url => updateSingleBlock(index, {url})}
                />
                <VariableChipList
                    variables={variables}
                    onButtonMouseDown={e => e.preventDefault()}
                    onSelect={insertVariable}
                />
            </div>
        )
    }

    const renderSingleBlock = (block: IContentBlock, index: number) => {
        switch (block.type) {
            case 'container':
                return renderContainer(block, index)
            case 'chart':
                return renderChart(block, index)
            case 'tag':
                return renderTag(block, index)
            case 'icon':
                return renderIcon(block, index)
            case 'image':
                return <ImageBlockField value={block.image} onChange={image => updateSingleBlock(index, {image})} />
            case 'button':
            case 'button_mo':
                return renderButton(block, index)
            default:
                return null
        }
    }

    return (
        <div className={`space-y-[12px] ${className}`}>
            {segments.map((segment, segIndex) => (
                <div
                    key={`${segment.kind}_${segmentKeyOf(segment)}`}
                    className={'border border-line02 rounded-[8px] p-[14px] space-y-[10px] bg-line04'}>
                    <div className={'flex items-center justify-between'}>
                        <Text className={'text-ti3 text-sub_blue'}>
                            {segment.kind === 'run'
                                ? BLOCK_TYPE_LABEL.paragraph
                                : BLOCK_TYPE_LABEL[blocks[segment.start].type]}
                        </Text>
                        <div className={'flex items-center gap-x-[10px]'}>
                            <button
                                type={'button'}
                                onClick={() => moveSegment(segIndex, -1)}
                                className={'p-[6px] rounded-[5px] border border-line02 hover:border-primary'}>
                                <WizImage src={ic_btn_next} className={'w-[10px] -rotate-90'} />
                            </button>
                            <button
                                type={'button'}
                                onClick={() => moveSegment(segIndex, 1)}
                                className={'p-[6px] rounded-[5px] border border-line02 hover:border-primary'}>
                                <WizImage src={ic_btn_next} className={'w-[10px] rotate-90'} />
                            </button>
                            <WizButton
                                size={'small'}
                                type={'red'}
                                text={'삭제'}
                                onClick={() => removeSegment(segment)}
                            />
                        </div>
                    </div>

                    {segment.kind === 'run' ? (
                        <RichParagraphRun
                            blocks={blocks.slice(segment.start, segment.end)}
                            onChange={next =>
                                onChange([...blocks.slice(0, segment.start), ...next, ...blocks.slice(segment.end)])
                            }
                            variables={variables}
                        />
                    ) : (
                        renderSingleBlock(blocks[segment.start], segment.start)
                    )}
                </div>
            ))}

            <div className={'flex items-center justify-end gap-x-[8px] pt-[4px] border-t border-line02'}>
                <Text className={'text-ti3 text-font03 whitespace-nowrap'}>추가할 블록:</Text>
                <WizSelect
                    customOption={BLOCK_TYPE_OPTIONS}
                    buttonClassName={'min-w-[180px]'}
                    value={BLOCK_TYPE_OPTIONS.find(o => o.id === typeToAdd)?.value}
                    onChange={value => {
                        const id = BLOCK_TYPE_OPTIONS.find(o => o.value === value)?.id
                        if (id) setTypeToAdd(id)
                    }}
                />
                <WizButton size={'small'} type={'primary'} text={'+ 블록 추가'} onClick={() => addBlock(typeToAdd)} />
            </div>
        </div>
    )
}

export default ContentBlockBuilder
